import { Chess } from 'chess.js';
import { GameLogic, MoveResult } from './GameLogic';
import chessAudio from '../utils/chessAudio';
import { trackChessMove, trackHintUsage, trackError } from '../utils/gtm';

// How long a wrong move stays on the board before being taken back
const WRONG_MOVE_DISPLAY_MS = 900;

/**
 * Move Handler Service
 * Single Responsibility: Handling chess moves and game progression
 * Dependency Inversion: Depends on GameLogic abstraction
 */
export class MoveHandler {
  constructor(gameState, uiState) {
    this.gameState = gameState;
    this.uiState = uiState;
    this.revertTimeout = null;
  }

  // True while a wrong move is displayed, before it is taken back
  isLocked() {
    return this.revertTimeout !== null;
  }

  cancelRevert() {
    clearTimeout(this.revertTimeout);
    this.revertTimeout = null;
  }

  attemptMove(sourceSquare, targetSquare) {
    const { game, moveIndex, currentLine, isPlayerTurn, opening } = this.gameState;
    
    if (!targetSquare || this.isLocked()) {
      return MoveResult.invalidMove();
    }

    const validation = GameLogic.validateMove(game, sourceSquare, targetSquare);
    
    if (!validation.success) {
      chessAudio.playIllegalSound();
      
      // Track illegal move
      trackChessMove(
        'illegal',
        opening?.name || 'unknown',
        currentLine?.name || 'unknown',
        moveIndex + 1,
        `${sourceSquare}-${targetSquare}`,
        false
      );
      
      return MoveResult.invalidMove();
    }

    // Check if in training mode (line loaded and moves remaining)
    if (currentLine && moveIndex < currentLine.moves.length) {
      if (!isPlayerTurn()) {
        return MoveResult.wrongTurn();
      }

      const expectedMove = currentLine.moves[moveIndex];
      const actualMove = validation.move.san;
      
      if (GameLogic.isCorrectMove(actualMove, expectedMove)) {
        return this._handleCorrectMove(validation, targetSquare);
      } else {
        return this._handleWrongMove(validation, targetSquare, expectedMove, actualMove);
      }
    } else {
      // No training line or line completed - block moves
      return MoveResult.invalidMove('Training complete! Select a new line to continue.');
    }
  }

  _handleCorrectMove(validation, targetSquare) {
    const { move, game } = validation;
    const { moveIndex, incrementMoveIndex, opening, currentLine } = this.gameState;
    
    // Track correct move
    const moveType = GameLogic.getMoveType(move);
    trackChessMove(
      moveType,
      opening?.name || 'unknown',
      currentLine?.name || 'unknown',
      moveIndex + 1,
      move.san,
      true
    );
    
    // Play sound
    this._playMoveSound(move);
    
    // Update game state
    this.gameState.setGame(game);
    incrementMoveIndex();
    const newMoveIndex = moveIndex + 1;
    
    // Show visual feedback
    this.uiState.setLastMove({ from: move.from, to: move.to });
    this.uiState.setMoveBadge({ square: targetSquare, type: 'correct' });
    
    // Check if line is complete
    if (this.gameState.currentLine && newMoveIndex >= this.gameState.currentLine.moves.length) {
      this._handleLineCompletion();
    }
    
    return MoveResult.correctMove(move, game, newMoveIndex);
  }

  _handleWrongMove(validation, targetSquare, expectedMove, actualMove) {
    const { opening, currentLine, moveIndex } = this.gameState;
    
    // Track wrong move
    trackChessMove(
      'wrong_move',
      opening?.name || 'unknown',
      currentLine?.name || 'unknown',
      moveIndex + 1,
      actualMove,
      false
    );
    
    // Play error sound
    chessAudio.playErrorSound();
    
    // Update mistake count
    this.gameState.incrementMistakes();
    
    // Show the wrong move on the board, then take it back
    this._showWrongMove(validation, targetSquare);
    
    const result = MoveResult.wrongMove(expectedMove, actualMove);
    if (this.gameState.mode === 'practice') {
      // Don't reveal the answer while practicing
      result.data.message = `Wrong move! ${actualMove} is not in this line. Try again.`;
    }
    return result;
  }

  _playMoveSound(move) {
    const moveType = GameLogic.getMoveType(move);
    
    switch (moveType) {
      case 'castle':
        chessAudio.playCastleSound();
        break;
      case 'check':
        chessAudio.playCheckSound();
        break;
      case 'capture':
        chessAudio.playCaptureSound();
        break;
      default:
        chessAudio.playMoveSound();
    }
  }

  _showWrongMove(validation, targetSquare) {
    const { game: previousGame, setGame } = this.gameState;
    const { lastMove: previousLastMove, setLastMove, setMoveBadge } = this.uiState;

    this.cancelRevert();
    setGame(validation.game);
    setLastMove({ from: validation.move.from, to: validation.move.to });
    setMoveBadge({ square: targetSquare, type: 'wrong' });

    this.revertTimeout = setTimeout(() => {
      this.revertTimeout = null;
      setGame(previousGame);
      setLastMove(previousLastMove);
      setMoveBadge(null);
    }, WRONG_MOVE_DISPLAY_MS);
  }

  _handleLineCompletion() {
    chessAudio.playGameEndSound();
    // Line completion logic will be handled by the main component
  }

  playHint() {
    const { game, moveIndex, currentLine, incrementMoveIndex, setGame, opening } = this.gameState;
    
    if (!currentLine || moveIndex >= currentLine.moves.length || this.isLocked()) {
      return null;
    }
    
    // Track hint usage
    trackHintUsage(
      opening?.name || 'unknown',
      currentLine.name,
      moveIndex + 1
    );
    
    const gameCopy = new Chess(game.fen());
    
    try {
      const hintMove = gameCopy.move(currentLine.moves[moveIndex]);
      this._playMoveSound(hintMove);
      
      setGame(gameCopy);
      incrementMoveIndex();
      this.uiState.setLastMove({ from: hintMove.from, to: hintMove.to });
      this.uiState.setMoveBadge(null);
      
      return {
        move: hintMove,
        game: gameCopy,
        message: `Hint: ${currentLine.moves[moveIndex]}`
      };
    } catch (error) {
      console.error('Failed hint move:', error);
      
      // Track hint error
      trackError(
        'hint_error',
        error.message,
        `Opening: ${opening?.name}, Line: ${currentLine.name}, Move: ${moveIndex + 1}`
      );
      
      return {
        error: `Error: Cannot play move ${currentLine.moves[moveIndex]}`
      };
    }
  }
}